import { Tabs, type TabsProps } from 'antd';
import { CanceledError } from 'axios';
import { Formik } from 'formik';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import './VendorLeadStepCondition.scss';

import { vendorsApi } from 'src/app/services/Vendors/Vendors.service';
import {
  vendorLeadProductConditionInitialValues,
  vendorLeadProductConditionValidationSchema,
} from 'src/components/vendors/steps/Condition/VendorLeadStepCondition.validation';
import { VendorLeadStepConditionSdsFilesList } from 'src/components/vendors/steps/Condition/VendorLeadStepConditionSdsFilesList';
import { VendorLeadStepFieldsTab } from 'src/components/vendors/steps/VendorLeadStepFieldsTab';
import { VendorLeadStepLayout } from 'src/components/vendors/steps/VendorLeadStepLayout';
import type { IVendorLeadConditionStepFormValues } from 'src/components/vendors/VendorLeadTypes';
import { toVendorLeadProductCreateOrUpdateSpecificationData } from 'src/components/vendors/VendorLeadValidation';
import { getFilesDiff } from 'src/helpers';
import { VendorsContext } from 'src/pages/VendorsPage/VendorsPage.context';
import { ProductSpecificationFieldGroup } from 'src/types/reference/ProductSpecificationFieldGroup';

const VendorLeadStepConditionSubSteps: Partial<Record<string, string>> = {
  'sds-files': ProductSpecificationFieldGroup.Condition,
};

type VendorLeadStepConditionProps = {
  vendorProductLeadId: string;
};

export function VendorLeadStepCondition({ vendorProductLeadId }: Readonly<VendorLeadStepConditionProps>) {
  const {
    isEditing,
    vendorLeadProduct,
    vendorLeadProductSpecification,
    updateVendorLeadProduct,
    setVendorStepGuideKey,
  } = useContext(VendorsContext);

  const [currentTab, setCurrentTab] = useState<string>(
    vendorLeadProductSpecification?.allowSdsFiles ? 'sds-files' : ProductSpecificationFieldGroup.Condition
  );

  const [, partialUpdateVendorProductLead, cancelPartialUpdateVendorProductLead] =
    vendorsApi.usePartialUpdateVendorProductLeadMutation(vendorProductLeadId);

  const save = useCallback(
    async (newValues: IVendorLeadConditionStepFormValues, submit: boolean) => {
      if (!vendorLeadProductSpecification) {
        throw new Error('Product specification is not found');
      }

      cancelPartialUpdateVendorProductLead();

      try {
        const updatedVendorProductLeadData = await partialUpdateVendorProductLead({
          data: {
            sdsFiles: getFilesDiff(newValues.sdsFiles, vendorLeadProduct?.sdsFiles ?? []),

            fieldGroupData: {
              fieldGroup: ProductSpecificationFieldGroup.Condition,
              specData: toVendorLeadProductCreateOrUpdateSpecificationData(
                newValues.specData,
                vendorLeadProductSpecification,
                ProductSpecificationFieldGroup.Condition
              ),
              submit,
            },
          },
        });

        if (submit) {
          updateVendorLeadProduct(updatedVendorProductLeadData.data);
        }
      } catch (unknownError) {
        if (unknownError instanceof CanceledError) {
          console.debug('Condition: auto save request has been cancelled');
        } else {
          console.error(unknownError);
        }
      }
    },
    [
      vendorLeadProductSpecification,
      cancelPartialUpdateVendorProductLead,
      partialUpdateVendorProductLead,
      vendorLeadProduct?.sdsFiles,
      updateVendorLeadProduct,
    ]
  );

  const handleValuesChange = useCallback(
    (saveValues: IVendorLeadConditionStepFormValues) => save(saveValues, false),
    [save]
  );

  const tabs: Exclude<TabsProps['items'], undefined> = useMemo(() => {
    const result: Exclude<TabsProps['items'], undefined> = [];

    if (vendorLeadProductSpecification?.allowSdsFiles) {
      result.push({
        key: 'sds-files',
        label: 'SDS Files',
        disabled: isEditing,
        children: <VendorLeadStepConditionSdsFilesList />,
      });
    }

    result.push({
      key: ProductSpecificationFieldGroup.Condition,
      label: 'Previously contained',
      disabled: isEditing,
      children: vendorLeadProductSpecification && (
        <VendorLeadStepFieldsTab
          fieldGroup={ProductSpecificationFieldGroup.Condition}
          specification={vendorLeadProductSpecification}
          onChange={handleValuesChange}
        />
      ),
    });

    return result;
  }, [isEditing, vendorLeadProductSpecification, handleValuesChange]);

  const handleSubmit = useCallback(
    async (submitValues: IVendorLeadConditionStepFormValues) => {
      await save(submitValues, true);

      const newSubStep = VendorLeadStepConditionSubSteps[currentTab];

      const moveToNextStep = newSubStep === undefined;

      if (!moveToNextStep) {
        setCurrentTab(newSubStep);
      }

      // Go to next step if empty
      return moveToNextStep;
    },
    [save, currentTab]
  );

  const handleTabChange = useCallback((newActiveKey: string) => {
    setCurrentTab(newActiveKey);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: false
  useEffect(() => {
    setVendorStepGuideKey(
      currentTab === 'sds-files'
        ? ProductSpecificationFieldGroup.SdsFiles
        : currentTab === ProductSpecificationFieldGroup.Condition
          ? ProductSpecificationFieldGroup.Condition
          : null
    );
  }, [currentTab]);

  if (!vendorLeadProduct || !vendorLeadProductSpecification) {
    return null;
  }

  return (
    <Formik<IVendorLeadConditionStepFormValues>
      initialValues={vendorLeadProductConditionInitialValues(
        vendorLeadProduct.sdsFiles,
        vendorLeadProductSpecification.fields,
        vendorLeadProduct
      )}
      validationSchema={vendorLeadProductConditionValidationSchema(vendorLeadProductSpecification.fields)}
      validateOnMount={true}
      enableReinitialize={true}
      onSubmit={handleSubmit}
    >
      <VendorLeadStepLayout>
        <div className="vendor-lead-step-condition__container">
          <Tabs
            items={tabs}
            animated={false}
            destroyInactiveTabPane={false}
            activeKey={currentTab}
            onChange={handleTabChange}
          />
        </div>
      </VendorLeadStepLayout>
    </Formik>
  );
}
